"use client";

import { CircleCheck as CheckCircle2, Lock, Loader } from 'lucide-react';
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { Textarea } from "@/components/ui/textarea";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const reassurances = ["Secure data handling", "Verified by youth leaders", "No commitment required", "Confirmation within 24h"];
function ConsultationForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="bg-card border border-border p-12 md:p-16 rounded-lg">
        <div className="flex flex-col items-center text-center gap-6">
          <div className="flex items-center justify-center size-14 rounded-lg bg-primary">
            <CheckCircle2 className="size-7 text-white" />
          </div>
          <h3 className="font-default text-3xl md:text-4xl text-foreground">
            Registration <span className="italic text-primary">received.</span>
          </h3>
          <p className="font-default text-base text-muted-foreground max-w-md">
            {successMessage}
          </p>
          <Button onClick={resetForm} variant="outline" className="mt-2">
            Register another member
          </Button>
        </div>
      </div>;
  }
  return <div className="bg-card border border-border p-8 md:p-12 rounded-lg shadow-md">
      <Form onSubmit={handleSubmit} className="space-y-8">
        {}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <FormField control={control} name="full_name" rules={{
          required: "Please enter the member's name"
        }} render={({
          field
        }) => <FormItem>
                <FormLabel className="font-default text-xs uppercase tracking-widest text-muted-foreground">
                  Full Name
                </FormLabel>
                <FormControl>
                  <Input placeholder="Alex Rivers" className="border-0 border-b-2 border-input px-0 h-11 bg-transparent focus-visible:ring-0 focus-visible:border-b-2 focus-visible:border-primary transition-colors font-default text-base" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>} />

          <FormField control={control} name="email_address" rules={{
          required: "Email is required",
          pattern: {
            value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
            message: "Please enter a valid email"
          }
        }} render={({
          field
        }) => <FormItem>
                <FormLabel className="font-default text-xs uppercase tracking-widest text-muted-foreground">
                  Parent Email
                </FormLabel>
                <FormControl>
                  <Input type="email" placeholder="parent@example.com" className="border-0 border-b-2 border-input px-0 h-11 bg-transparent focus-visible:ring-0 focus-visible:border-b-2 focus-visible:border-primary transition-colors font-default text-base" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>} />
        </div>

        {}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <FormField control={control} name="phone_number" rules={{
          required: "Phone number is required"
        }} render={({
          field
        }) => <FormItem>
                <FormLabel className="font-default text-xs uppercase tracking-widest text-muted-foreground">
                  Emergency Phone
                </FormLabel>
                <FormControl>
                  <Input type="tel" placeholder="(555) 000-0000" className="border-0 border-b-2 border-input px-0 h-11 bg-transparent focus-visible:ring-0 focus-visible:border-b-2 focus-visible:border-primary transition-colors font-default text-base" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>} />

          <FormField control={control} name="case_type" rules={{
          required: "Please select an activity"
        }} render={({
          field
        }) => <FormItem>
                <FormLabel className="font-default text-xs uppercase tracking-widest text-muted-foreground">
                  Primary Interest
                </FormLabel>
                <FormControl>
                  <Select onValueChange={field.onChange} value={field.value || ""}>
                    <SelectTrigger className="border-0 border-b-2 border-input px-0 h-11 bg-transparent focus-visible:ring-0 focus-visible:border-b-2 focus-visible:border-primary transition-colors font-default text-base w-full">
                      <SelectValue placeholder="Select an activity" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Summer Camp">Summer Camp</SelectItem>
                      <SelectItem value="Team Sports">Team Sports</SelectItem>
                      <SelectItem value="Live Music Nights">Live Music Nights</SelectItem>
                      <SelectItem value="Volunteer Projects">Volunteer Projects</SelectItem>
                    </SelectContent>
                  </Select>
                </FormControl>
                <FormMessage />
              </FormItem>} />
        </div>

        {}
        <FormField control={control} name="brief_description" rules={{
        required: "Please provide a brief note",
        minLength: {
          value: 20,
          message: "Minimum 20 characters"
        },
        maxLength: {
          value: 500,
          message: "Maximum 500 characters"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="font-default text-xs uppercase tracking-widest text-muted-foreground">
                Additional Notes
              </FormLabel>
              <FormControl>
                <Textarea rows={6} placeholder="Any allergies, medical conditions, or specific interests we should know about?" className="border-0 border-b-2 border-input px-0 py-2 bg-transparent focus-visible:ring-0 focus-visible:border-b-2 focus-visible:border-primary transition-colors font-default text-base placeholder:italic resize-none" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        {}
        <FormField control={control} name="preferred_contact" rules={{
        required: "Please select a contact preference"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="font-default text-xs uppercase tracking-widest text-muted-foreground">
                Preferred contact method
              </FormLabel>
              <FormControl>
                <RadioGroup onValueChange={field.onChange} value={field.value || "Email"} className="flex flex-col sm:flex-row gap-4 sm:gap-8 pt-2">
                  <div className="flex items-center gap-3" data-index={0}>
                    <RadioGroupItem value="Email" id="contact-email" className="size-[18px]" />
                    <Label htmlFor="contact-email" className="font-default text-base cursor-pointer">
                      Email
                    </Label>
                  </div>
                  <div className="flex items-center gap-3" data-index={1}>
                    <RadioGroupItem value="Phone" id="contact-phone" className="size-[18px]" />
                    <Label htmlFor="contact-phone" className="font-default text-base cursor-pointer">
                      Phone
                    </Label>
                  </div>
                </RadioGroup>
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <Separator className="bg-border" />

        {}
        <FormField control={control} name="confidentiality_acknowledged" rules={{
        validate: v => v === true || "Please acknowledge to proceed"
      }} render={({
        field
      }) => <FormItem>
              <div className="flex items-start gap-3">
                <FormControl>
                  <Checkbox checked={field.value || false} onCheckedChange={field.onChange} className="mt-0.5" />
                </FormControl>
                <Label className="font-default text-[13px] leading-relaxed text-muted-foreground cursor-pointer">
                  I confirm that I have read and signed the medical release and registration forms for Youth Tribe.
                </Label>
              </div>
              <FormMessage />
            </FormItem>} />

        {submitError && <div className="p-4 border border-destructive/30 rounded-lg bg-destructive text-destructive-foreground">
            <p className="font-default text-sm">{submitError}</p>
          </div>}

        <div className="space-y-3 pt-2">
          <Button type="submit" disabled={isSubmitting} className="w-full h-14 font-default text-base bg-primary text-white hover:bg-primary/90">
            {isSubmitting ? <>
                <Loader className="animate-spin mr-2 size-4" />
                Processing...
              </> : <>
                <Lock className="size-4 mr-2" />
                Submit Registration
              </>}
          </Button>
          <p className="font-default italic text-xs text-muted-foreground text-center">
            Your data is encrypted and stored securely.
          </p>
        </div>
      </Form>
    </div>;
}
export default function RegistrationForms() {
  const {
    ref: leftRef,
    isVisible: leftVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const {
    ref: rightRef,
    isVisible: rightVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  return <section data-section-id="46" id="registration-forms" className="bg-background py-24 md:py-32 lg:py-[140px] px-6 md:px-10 lg:px-16">
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          {}
          <div ref={leftRef} className={`lg:col-span-5 transition-all duration-1000 ease-out ${leftVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
            <div className="lg:sticky lg:top-28">
              <p className="font-default text-xs uppercase tracking-[0.3em] mb-8 text-primary">
                — Join the Tribe
              </p>

              <h1 className="font-default text-5xl md:text-6xl lg:text-[64px] leading-[0.9] text-foreground mb-8">
                Ready to
                <br />
                <span className="italic text-primary">belong?</span>
              </h1>

              <p className="font-default text-[17px] leading-relaxed text-muted-foreground max-w-[400px] mb-10">
                Fill out the registration form below to secure your spot in our upcoming activities. Whether it's summer camp or Friday night socials, we're excited to have you in the Youth Tribe.
              </p>

              <ul className="space-y-3 mb-12">
                {reassurances.map((item, i) => <li key={i} data-index={i} className="flex items-center gap-3 font-default text-sm text-foreground">
                    <span className="inline-block size-1.5 rounded-lg shrink-0 bg-primary" />
                    {item}
                  </li>)}
              </ul>

              <Separator className="bg-border mb-8 max-w-[400px]" />

              <p className="font-default italic text-lg leading-relaxed max-w-[400px] text-primary">
                Questions? Reach out to our team at (555) 678-9012.
              </p>
            </div>
          </div>

          {}
          <div ref={rightRef} className={`lg:col-span-7 transition-all duration-1000 delay-150 ease-out ${rightVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
            <FormProvider formKey="registration_form_parent_hub" formId="registration_form_parent_hub" sectionName="registration-forms" defaultValues={{
            full_name: "",
            email_address: "",
            phone_number: "",
            case_type: "",
            brief_description: "",
            preferred_contact: "Email",
            confidentiality_acknowledged: false
          }} validationRules={{
            full_name: {
              required: true
            },
            email_address: {
              required: true,
              pattern: "email"
            },
            phone_number: {
              required: true,
              pattern: "phone"
            },
            case_type: {
              required: true
            },
            brief_description: {
              required: true,
              minLength: 20,
              maxLength: 500
            },
            preferred_contact: {
              required: true
            }
          }} submitText="Submit Registration" successMessage="Your registration has been received. We'll be in touch shortly with next steps." errorMessage="Something went wrong. Please try again or contact us directly.">
              <ConsultationForm />
            </FormProvider>
          </div>
        </div>
      </div>
    </section>;
}